import React, { useState, useRef, useEffect } from 'react'
import { MoreVertical, Edit3, Trash2, ImageIcon } from 'lucide-react'
import { cn } from '@/utils/cn'
import { ClassItem } from '@/types/catalog'
import { Category, ClassThumbnail } from '@/types'
import { formatClassNameForDisplay } from '@/utils/classNameUtils'
import AuthenticatedImage from '@/components/AuthenticatedImage'

interface MobileCatalogClassItemProps {
  classItem: ClassItem
  category: Category | null
  thumbnail: ClassThumbnail | null
  isAdmin: boolean
  onTap: () => void
  onEdit: () => void
  onDelete: () => Promise<void>
}

const MobileCatalogClassItem: React.FC<MobileCatalogClassItemProps> = ({
  classItem,
  category,
  thumbnail,
  isAdmin,
  onTap,
  onEdit,
  onDelete,
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  const handleDelete = async () => {
    if (!confirm(`Delete "${classItem.displayName}"? This will also delete all images in it.`)) {
      return
    }

    setIsDeleting(true)
    setShowMenu(false)
    try {
      await onDelete()
    } finally {
      setIsDeleting(false)
    }
  }

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowMenu(!showMenu)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return (
    <div
      onClick={isDeleting ? undefined : onTap}
      className={cn(
        'flex items-center gap-3 px-4 py-3 bg-white',
        !isDeleting && 'active:bg-gray-50 cursor-pointer',
        isDeleting && 'opacity-50'
      )}
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-gray-100 overflow-hidden">
        {thumbnail?.url ? (
          <AuthenticatedImage
            imageUrl={thumbnail.url}
            alt={classItem.displayName}
            className="w-full h-full object-cover"
            size="thumb"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-6 w-6 text-gray-300" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <h3 className="font-medium text-gray-900 truncate">
            {formatClassNameForDisplay(classItem.displayName)}
          </h3>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {category && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
              style={{
                backgroundColor: `${category.color}20`,
                color: category.color,
              }}
            >
              {category.name}
            </span>
          )}
          {classItem.tag && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
              {classItem.tag}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
          <span>{classItem.imageCount} images</span>
          <span className="text-gray-300">|</span>
          <span>{formatDate(classItem.lastModified)}</span>
        </div>
      </div>

      {/* Actions Menu */}
      {isAdmin && (
        <div ref={menuRef} className="relative flex-shrink-0">
          <button
            onClick={handleMenuClick}
            className="p-2 -mr-2 text-gray-400 active:bg-gray-100 rounded-lg"
          >
            <MoreVertical className="h-5 w-5" />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border z-50">
              <div className="py-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMenu(false)
                    onEdit()
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-gray-700 active:bg-gray-50"
                >
                  <Edit3 className="h-4 w-4 text-gray-500" />
                  <span>Edit class</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDelete()
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-600 active:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete class</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MobileCatalogClassItem
